import { useState } from 'react'
import { Base, WarningCircleIcon } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { ScopePicker } from './scope-picker'
import { ApiKeyRow, findByName, formatCreated } from './types'

const {
  Button,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter,
  Input,
  Callout
} = Base

interface CreateKeyDialogProps {
  isOpen: boolean
  // The group in scope: a merchant Admin's own, or the one the selected ecommerce belongs to.
  group: string
  keys: ApiKeyRow[]
  saving: boolean
  onClose: () => void
  onCreate: (name: string, scopes: string[]) => void
  onViewExisting: (key: ApiKeyRow) => void
}

export const CreateKeyDialog: React.FC<CreateKeyDialogProps> = ({
  isOpen,
  group,
  keys,
  saving,
  onClose,
  onCreate,
  onViewExisting
}) => {
  const { t, i18n } = useTranslation()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>([])

  // Creating a name the group already holds returns that existing key, so the duplicate is shown
  // before issuing rather than after: otherwise "Create" silently hands back another key.
  const duplicate = findByName(keys, group, name)
  const canCreate = name.trim().length > 0 && !duplicate && scopes.length > 0

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setName('')
          setScopes([])
          onClose()
        }
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup>
          <div className="flex w-[36rem] max-w-full flex-col gap-4">
            <DialogCloseButton />
            <DialogHeader>
              <DialogTitle>{t('api_keys.create_dialog.title')}</DialogTitle>
              <DialogDescription>
                {t('api_keys.create_dialog.description')}
              </DialogDescription>
            </DialogHeader>

            {/* Fixed rather than a picker: a user's claim carries one group, so the server can
                only ever offer them this one. */}
            <div className="space-y-1.5">
              <label className="inline-block text-sm" htmlFor="api-key-group">
                {t('api_keys.create_dialog.group_label')}
              </label>
              <Input
                id="api-key-group"
                name="api-key-group"
                aria-label={t('api_keys.create_dialog.group_label')}
                value={group}
                disabled
              />
            </div>

            <div className="space-y-1.5">
              <label className="inline-block text-sm" htmlFor="api-key-name">
                {t('api_keys.create_dialog.name_label')}
              </label>
              <Input
                id="api-key-name"
                name="api-key-name"
                aria-label={t('api_keys.create_dialog.name_label')}
                autoComplete="off"
                placeholder={t('api_keys.create_dialog.name_placeholder')}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <p className="text-xs text-neutral-600">
                {t('api_keys.create_dialog.name_help')}
              </p>
            </div>

            {duplicate ? (
              <Callout tone="warning" icon={<WarningCircleIcon />}>
                <div className="flex flex-col gap-1">
                  <p className="font-medium">
                    {t('api_keys.create_dialog.duplicate_title', {
                      name: duplicate.name,
                      group: duplicate.group
                    })}
                  </p>
                  <p className="text-xs">
                    {t('api_keys.create_dialog.duplicate_body', {
                      created: formatCreated(duplicate.createdAt, i18n.language)
                    })}
                  </p>
                </div>
              </Callout>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm">
                  {t('api_keys.create_dialog.access_label')}
                </span>
                <ScopePicker scopes={scopes} onChange={setScopes} />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('api_keys.create_dialog.cancel')}
              </Button>
              {duplicate ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => onViewExisting(duplicate)}
                >
                  {t('api_keys.create_dialog.view_existing')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  disabled={!canCreate}
                  loading={saving}
                  onClick={() => onCreate(name.trim(), scopes)}
                >
                  {t('api_keys.create_dialog.submit')}
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
